import * as React from "react";
import { AlertCircle, Armchair, Loader2 } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import {
  useCreateSeat,
  useUpdateSeat,
  type CreateSeatInput,
  type Seat,
  type UpdateSeatInput,
} from "@/lib/seating/seats";
import type { Zone } from "@/lib/seating/zones";

/**
 * Phase F-finale-7 Slice 7b.4b — Seat create / edit sheet.
 *
 * Right-side drawer mirroring `<ZoneEditorSheet>`: keeps the Seats
 * `<DataTable>` visible behind the sheet while the admin types. Create mode
 * requires picking the parent zone (pre-selected from the page's active zone
 * filter via `defaultZoneId`); a seat can't be moved between zones, so the
 * zone field is shown read-only in edit mode. Edit mode PATCHes only the
 * changed fields, and a zero-diff save closes silently.
 *
 * The BE (`/api/seats`, `manage_zones`) re-validates the field bounds and
 * the (zone, seat_number) uniqueness, so server-side 409/422s land in the
 * top-of-sheet error banner.
 */

interface DraftState {
  zone_id: string;
  seat_number: string;
  seat_type: string;
  is_active: boolean;
}

const EMPTY_DRAFT: DraftState = {
  zone_id: "",
  seat_number: "",
  seat_type: "standard",
  is_active: true,
};

function draftFromExisting(existing: Seat | null | undefined): DraftState {
  if (!existing) return EMPTY_DRAFT;
  return {
    zone_id: existing.zone_id,
    seat_number: existing.seat_number,
    seat_type: existing.seat_type,
    is_active: existing.is_active,
  };
}

function buildCreatePayload(draft: DraftState): CreateSeatInput {
  return {
    zone_id: draft.zone_id,
    seat_number: draft.seat_number.trim(),
    seat_type: draft.seat_type.trim() || "standard",
  };
}

function buildUpdatePayload(draft: DraftState, existing: Seat): UpdateSeatInput {
  const payload: UpdateSeatInput = {};

  const seatNumber = draft.seat_number.trim();
  if (seatNumber && seatNumber !== existing.seat_number) payload.seat_number = seatNumber;

  const seatType = draft.seat_type.trim() || "standard";
  if (seatType !== existing.seat_type) payload.seat_type = seatType;

  if (draft.is_active !== existing.is_active) payload.is_active = draft.is_active;

  return payload;
}

export interface SeatEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Zones for the create-mode selector + edit-mode name resolution. */
  zones: Zone[];
  existing?: Seat | null;
  /** Pre-select a zone in create mode (the page's active zone filter). */
  defaultZoneId?: string | null;
  onSaved?: (saved: Seat) => void;
}

export function SeatEditorSheet({
  open,
  onOpenChange,
  zones,
  existing,
  defaultZoneId,
  onSaved,
}: SeatEditorSheetProps) {
  const isEdit = Boolean(existing);

  const [draft, setDraft] = React.useState<DraftState>(() => draftFromExisting(existing));
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(
        existing ? draftFromExisting(existing) : { ...EMPTY_DRAFT, zone_id: defaultZoneId ?? "" },
      );
      setSubmitError(null);
    }
  }, [open, existing, defaultZoneId]);

  const createMutation = useCreateSeat();
  const updateMutation = useUpdateSeat();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleField = <K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const trimmedNumber = draft.seat_number.trim();
  const canSubmit =
    trimmedNumber.length > 0 &&
    trimmedNumber.length <= 40 &&
    (isEdit || draft.zone_id.length > 0) &&
    !isSubmitting;

  const resolvedZoneName = zones.find((z) => z.id === draft.zone_id)?.name ?? draft.zone_id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canSubmit) return;

    try {
      if (existing) {
        const body = buildUpdatePayload(draft, existing);
        if (Object.keys(body).length === 0) {
          onOpenChange(false);
          return;
        }
        const saved = await updateMutation.mutateAsync({ id: existing.id, body });
        onSaved?.(saved);
        toast.success(`Saved changes to seat ${saved.seat_number}.`);
        onOpenChange(false);
      } else {
        const saved = await createMutation.mutateAsync(buildCreatePayload(draft));
        onSaved?.(saved);
        toast.success(`Added seat ${saved.seat_number}.`);
        onOpenChange(false);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? friendlyApiMessage(err)
          : err instanceof Error
            ? err.message
            : "Couldn't save the seat. Please retry.";
      setSubmitError(message);
    }
  };

  const title = isEdit ? "Edit seat" : "Add seat";
  const description = isEdit
    ? `Update seat ${existing?.seat_number ?? ""}. A seat can't be moved to another zone once created.`
    : "Add a seat to a zone. Seat numbers are unique within their zone.";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]"
        data-testid="seat-editor-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <Armchair className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">{title}</SheetTitle>
              <SheetDescription className="mt-1 text-xs">{description}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
          data-testid="seat-editor-form"
        >
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            {isEdit ? (
              <Field
                label="Zone"
                htmlFor="seat-zone-readonly"
                helper="A seat can't be moved to another zone once created."
              >
                <Input id="seat-zone-readonly" value={resolvedZoneName} readOnly disabled />
              </Field>
            ) : (
              <Field
                label="Zone"
                htmlFor="seat-zone"
                required
                helper="The zone this seat belongs to."
              >
                <Select value={draft.zone_id} onValueChange={(v) => handleField("zone_id", v)}>
                  <SelectTrigger id="seat-zone" aria-label="Zone">
                    <SelectValue placeholder="Select a zone…" />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map((z) => (
                      <SelectItem key={z.id} value={z.id}>
                        {z.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <Field label="Seat number" htmlFor="seat-number" required>
              <Input
                id="seat-number"
                value={draft.seat_number}
                maxLength={40}
                required
                autoFocus
                onChange={(e) => handleField("seat_number", e.target.value)}
                placeholder="A-01"
              />
            </Field>

            <Field
              label="Seat type"
              htmlFor="seat-type"
              helper="e.g. standard, premium, accessible. Defaults to standard."
            >
              <Input
                id="seat-type"
                value={draft.seat_type}
                maxLength={40}
                onChange={(e) => handleField("seat_type", e.target.value)}
                placeholder="standard"
              />
            </Field>

            {isEdit ? (
              <label className="border-border/60 bg-muted/20 flex items-start gap-3 rounded-md border px-3 py-2.5">
                <Switch
                  checked={draft.is_active}
                  onCheckedChange={(v) => handleField("is_active", v)}
                  aria-label="Active"
                />
                <div className="grid gap-0.5">
                  <span className="text-sm font-medium">Active</span>
                  <span className="text-muted-foreground text-xs">
                    Inactive seats are hidden from new bookings and allocations.
                  </span>
                </div>
              </label>
            ) : null}
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              {isEdit ? "Save changes" : "Add seat"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  children: React.ReactNode;
}

function Field({ label, htmlFor, required, helper, children }: FieldProps) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
      {helper ? <p className="text-muted-foreground mt-1 text-xs">{helper}</p> : null}
    </div>
  );
}

function friendlyApiMessage(err: ApiError): string {
  // The seating BE returns a generic top-level `CONFLICT` code and carries the
  // specific reason in `details.code` (verified live: a duplicate seat_number
  // yields `{ code: "CONFLICT", details: { code: "seat.seat_number_taken" } }`),
  // so match the nested code — not `err.code` — for the tailored copy.
  const detailCode = typeof err.details?.code === "string" ? err.details.code : undefined;
  if (detailCode === "seat.seat_number_taken") {
    return "A seat with that number already exists in this zone.";
  }
  // The envelope's top-level message is already user-friendly for the remaining
  // cases (permission denied, validation), so fall back to it verbatim.
  return err.message || "Couldn't save the seat. Please retry.";
}
